import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Upload, Trash2, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";

interface PreviewImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: File | null;
  data?: any[];
  errorCount?: number;
  onRemoveFile: () => void;
  onImport: () => void;
  isImporting?: boolean;
}

export const PreviewImportModal: React.FC<PreviewImportModalProps> = ({
  open,
  onOpenChange,
  file,
  data = [],
  errorCount = 0,
  onRemoveFile,
  onImport,
  isImporting = false,
}) => {
  const previewData = useMemo(() => {
    return data.map((item: any) => ({
      id: item.id,
      glCode: item.glCode,
      glName: item.glName,
      glType: item.glTypeDisplay,
      source: item.sourceDisplay,
      category: item.transactionCategoryDisplay,
      type: item.transactionType,
      status: item.statusDisplay,
      valid: true, // Assuming valid for now since it's from the backend, could add validation flag if available
    }));
  }, [data]);

  const columns: TableColumn<any>[] = [
    {
      key: "glCode",
      header: "GL Code",
      render: (val: any) => (
        <span className="text-blue-600 font-bold text-sm">{val}</span>
      ),
    },
    {
      key: "glName",
      header: "GL Name",
      render: (val: any) => (
        <span className="text-gray-900 text-sm">{val}</span>
      ),
    },
    {
      key: "glType",
      header: "GL Type",
      render: (val: any) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#f0f7ff] text-[#1a73e8]">
          {val}
        </span>
      ),
    },
    {
      key: "source",
      header: "Source",
    },
    {
      key: "category",
      header: "Transaction Category",
    },
    {
      key: "type",
      header: "Transaction Type",
    },
    {
      key: "valid",
      header: "Valid",
      render: () => <CheckCircle2 className="w-4 h-4 text-[#1e8e3e]" />,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl gap-0 p-0 overflow-hidden bg-white">
        <DialogHeader className="p-6 border-b border-gray-100 bg-white pb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#f0f7ff] text-[#1a73e8]">
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-xl font-bold text-gray-900">
                Bulk Import GL Codes
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Download a pre-filled template, add your GL Code & Name, then
                upload
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 bg-white overflow-y-auto max-h-[70vh]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-gray-800">
                {file?.name || "Uploaded File.csv"}
              </span>
              <span className="text-sm text-gray-400 font-medium">
                {data.length + errorCount} rows
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#e6f4ea] text-[#1e8e3e] text-xs font-bold">
                <CheckCircle2 className="w-3.5 h-3.5" /> {data.length} valid
              </span>
              {errorCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-600 text-xs font-bold">
                  <XCircle className="w-3.5 h-3.5" /> {errorCount} errors
                </span>
              )}
            </div>
            <button
              onClick={onRemoveFile}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 font-medium transition-colors"
            >
              <Trash2 className="w-4 h-4" /> Remove file
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <GenericTable data={previewData} columns={columns} rowKey="id" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-end gap-3">
          <Button
            variant="outline"
            className="text-gray-700 border-gray-300 font-medium"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="bg-[#1a73e8] hover:bg-[#1557b0] text-white font-medium shadow-sm"
            onClick={onImport}
            disabled={isImporting}
          >
            {isImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {isImporting ? "Importing..." : `Import ${data.length} GL Codes`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
